<template>
    <div class="camera-stream-view video-file-player-view">
        <div class="camera-stream-view__head">
            <h4 class="mb-0">{{ video.name || ('Video ' + video.id) }}</h4>
            <div class="camera-stream-view__meta text-muted">
                <span>File playback</span>
                <span v-if="video.path" class="camera-stream-view__path" :title="video.path">
                    {{ shortPath }}
                </span>
            </div>
        </div>

        <div class="camera-stream-view__stage">
            <video
                v-if="playbackUrl && !errorMsg"
                ref="player"
                class="video-file-player-view__video"
                controls
                playsinline
                preload="auto"
                :src="playbackUrl"
                @loadstart="onLoadStart"
                @loadeddata="onReady"
                @canplay="onReady"
                @error="onVideoError"
            />
            <div v-if="loading && !errorMsg" class="camera-stream-view__loading video-file-player-view__spinner">
                Loading video…
            </div>
            <div v-if="errorMsg" class="camera-stream-view__loading">
                {{ errorMsg }}
            </div>
            <div v-if="!playbackUrl && !errorMsg" class="camera-stream-view__loading">
                No playback URL
            </div>
        </div>

        <div class="camera-stream-view__foot">
            <button type="button" class="btn btn-border btn-sm" @click="close">Close</button>
        </div>
    </div>
</template>

<script>
import { bus } from '../../bus';

export default {
    props: {
        video: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            errorMsg: '',
            loading: true,
            cacheBust: Date.now(),
        };
    },
    computed: {
        playbackUrl() {
            const id = this.video && this.video.id;
            if (!id) {
                return '';
            }
            // Always HTTP stream endpoint — never a container/host filesystem path.
            // Cache-bust so a server-side re-encode is not stuck behind a prior
            // MEDIA_ERR_SRC_NOT_SUPPORTED / cached partial response.
            const bust =
                this.video.size ||
                this.video.updated_at ||
                this.video.date ||
                this.cacheBust;
            return `/videos/${encodeURIComponent(String(id))}/stream?v=${encodeURIComponent(String(bust))}`;
        },
        shortPath() {
            const p = String(this.video.path || '');
            return p.length > 64 ? p.slice(0, 61) + '…' : p;
        },
    },
    methods: {
        close() {
            const el = this.$refs.player;
            if (el && typeof el.pause === 'function') {
                el.pause();
            }
            bus.$emit('openpopup', { open: false });
        },
        onLoadStart() {
            this.loading = true;
            this.errorMsg = '';
        },
        onReady() {
            this.loading = false;
        },
        onVideoError() {
            this.loading = false;
            const el = this.$refs.player;
            const code = el && el.error ? el.error.code : 0;
            const httpHint = this.playbackUrl;
            if (code === 4) {
                this.errorMsg =
                    'Unable to play this file in the browser (unsupported codec). Convert to H.264/AAC MP4.';
            } else if (code === 2) {
                this.errorMsg = `Network error loading ${httpHint}`;
            } else if (code === 3) {
                this.errorMsg = 'Video decode error (corrupt or unsupported bitstream)';
            } else {
                this.errorMsg = `Unable to load video (${httpHint}). File may be missing.`;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.video-file-player-view__video {
    display: block;
    width: 100%;
    max-height: 70vh;
    background: #000;
    position: relative;
    z-index: 1;
}
.video-file-player-view__spinner {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 2;
}
</style>
